import { ChangeDetectionStrategy, Component, ElementRef, effect, input, viewChild } from '@angular/core';

/**
 * Mostra um e-mail já enviado, com a formatação que o destinatário viu.
 *
 * O conteúdo é HTML de terceiros do ponto de vista da aplicação — vem de um template gravado no
 * banco —, e por isso NÃO entra na página: vai para dentro de um `iframe` isolado.
 *
 * Três decisões de segurança, juntas:
 *
 * - `sandbox` **vazio** liga todas as restrições: sem script, sem formulário, sem navegação da
 *   janela de cima, e com origem opaca — o documento de dentro não alcança nada daqui.
 * - O HTML é escrito em `srcdoc` pela **propriedade do elemento**, não por binding de template.
 *   Binding passaria pelo sanitizador do Angular e exigiria `bypassSecurityTrust`, que o projeto
 *   proíbe (e com razão: ele desliga a proteção para a página INTEIRA). Aqui não há bypass nenhum —
 *   o conteúdo simplesmente nunca é interpretado no contexto da aplicação.
 * - Nada de `innerHTML`: o e-mail não vira nó do DOM desta página em momento algum.
 *
 * Limite conhecido: a CSP da aplicação (`default-src 'self'`) vale também dentro do frame, então
 * imagem hospedada fora do domínio não carrega. O texto e a formatação aparecem; um logotipo remoto
 * fica como espaço vazio.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-visualizador-email',
    standalone: true,
    template: `<iframe #quadro class="visualizador-email-quadro" sandbox referrerpolicy="no-referrer" [title]="titulo()"></iframe>`,
    styleUrl: './visualizador-email.scss'
})
export class VisualizadorEmail {
    /** HTML do e-mail. Vazio deixa o frame em branco, sem erro. */
    readonly html = input<string>('');
    /** Rótulo acessível do frame — quem navega por leitor de tela precisa saber o que há dentro. */
    readonly titulo = input<string>('E-mail enviado');

    private readonly quadro = viewChild<ElementRef<HTMLIFrameElement>>('quadro');

    constructor() {
        effect(() => {
            const iframe = this.quadro()?.nativeElement;
            if (!iframe) {
                return;
            }
            // Reatribuir `srcdoc` recarrega o documento do frame: trocar de e-mail não deixa resto do
            // anterior na tela.
            iframe.srcdoc = this.html();
        });
    }
}
